/**
 * Centralised feature-flag access (Feb 2026 — Phase 1).
 *
 * Every UK / EU surface in the app pulls its visibility from here so a
 * single admin toggle removes UK from sidebar, dashboard widgets,
 * routes, and settings sections in one shot. No more per-component
 * `feature_flags?.uk_workflows !== false` checks scattered around.
 *
 * Defaults intentionally match the backend gating:
 *   - `uk_workflows` defaults to ON when the flag is missing (existing
 *     workspaces predate the toggle).
 *   - `eu_lane` defaults to OFF.
 *
 * Pure module — no React state of its own; reads from AuthContext.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resolveFeatureFlags } from './featureFlagsCore';

export { resolveFeatureFlags };

export function useFeatureFlags() {
  const { activeWorkspace } = useAuth();
  return React.useMemo(
    () => resolveFeatureFlags(activeWorkspace),
    [activeWorkspace],
  );
}

/**
 * Route guard — wrap a route element with this when the route belongs
 * to a feature-flagged module. If the module is disabled for the
 * active workspace, users are redirected to /dashboard rather than
 * seeing a broken page.
 *
 * Usage:
 *   <Route path="/rgr-returns"
 *     element={<RequireModule module="uk_workflows"><RGRReturnsPage /></RequireModule>} />
 */
export function RequireModule({ module, children, fallback = '/dashboard' }) {
  const { ukWorkflows, euLane } = useFeatureFlags();
  const { isCrossWorkspaceMode } = useAuth();

  // System admin viewing the global "all workspaces" mode keeps full
  // visibility — they're cross-tenant ops, not bound to per-workspace flags.
  if (isCrossWorkspaceMode) return children;

  let enabled = true;
  if (module === 'uk_workflows') enabled = ukWorkflows;
  else if (module === 'eu_lane') enabled = euLane;

  if (!enabled) return <Navigate to={fallback} replace />;
  return children;
}
